"use client";

import { useTranslations } from "next-intl";

export interface OvermoldingDesignGuidelinesProps {
	translationNamespace?: string;
	/** 为 true 时表头第一列显示 colSpecification（嵌件注塑页等）；默认 false 与包胶页一致（仅 sr-only） */
	showSpecificationColumnHeader?: boolean;
	/**
	 * 嵌件注塑页专用：Depth 单行；Projected 合并为 US/Metric 各一格内换行；表体白/灰斑马纹（与设计稿一致）
	 */
	insertMoldingCompactTable?: boolean;
}

const thBase =
	"px-3 py-4 text-center text-xs font-bold leading-snug text-white sm:px-4 sm:py-5 sm:text-sm md:text-base";
const tdLabel =
	"border-b border-gray-200 bg-[#fdfdfd] px-3 py-5 align-middle text-xs font-bold text-black sm:px-4 sm:py-6 sm:text-sm md:py-7";
const tdDataBase =
	"border-b border-gray-200 px-3 py-5 align-middle text-center text-xs font-normal text-black sm:px-4 sm:py-6 sm:text-sm md:py-7";
const tdDataUs = `${tdDataBase} bg-[#ededed]`;
const tdDataMetric = `${tdDataBase} bg-[#fdfdfd]`;

const zebraTdBase =
	"border-b border-gray-200 px-3 py-5 align-middle text-xs sm:px-4 sm:py-6 sm:text-sm md:py-7";

/** 斑马纹：第 1、3 行浅灰，第 2、4 行白底（与设计稿一致） */
function zebraLabelClass(_rowIndex: number, extra = "") {
	const bg = "bg-[#fdfdfd]";
	return `${zebraTdBase} text-left font-bold text-black ${bg} ${extra}`.trim();
}

function zebraDataColumnClass(_rowIndex: number, column: "us" | "metric", extra = "") {
	const bg = column === "us" ? "bg-[#ededed]" : "bg-[#fdfdfd]";
	return `${zebraTdBase} whitespace-pre-line text-center font-normal text-black ${bg} ${extra}`.trim();
}

/**
 * JS Precision Overmolding Design Guidelines：与钣金 Design Guidelines 类似的绿表头圆角表；
 * 含 Depth / Projected Mold Area 的 rowspan 子行，以及表下「Additional Design Specifications」四条说明。
 */
export default function OvermoldingDesignGuidelines({
	translationNamespace = "overmoldingDesignGuidelinesSection",
	showSpecificationColumnHeader = false,
	insertMoldingCompactTable = false,
}: OvermoldingDesignGuidelinesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("title")}
				</h2>
				<p className="mb-6 text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("description")}
				</p>

				<div className="overflow-hidden rounded-[15px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] sm:shadow-[0_6px_32px_rgba(0,0,0,0.1)]">
					<table className="w-full border-separate border-spacing-0 text-left">
						<thead>
							<tr>
								<th scope="col" className={`${thBase} w-[28%] rounded-tl-[15px] bg-[#2d5a1f]`}>
									{showSpecificationColumnHeader ? (
										<span className="block">{t("colSpecification")}</span>
									) : (
										<span className="sr-only">{t("colSpecSrOnly")}</span>
									)}
								</th>
								<th scope="col" className={`${thBase} bg-[#4a8c2a]`}>
									{t("colUsCustomary")}
								</th>
								<th scope="col" className={`${thBase} rounded-tr-[15px] bg-[#6dad3c]`}>
									{t("colMetric")}
								</th>
							</tr>
						</thead>
						<tbody>
							{insertMoldingCompactTable ? (
								<>
					<tr>
						<td className={zebraLabelClass(0)}>{t("maxSizeLabel")}</td>
						<td className={zebraDataColumnClass(0, "us")}>{t("maxSizeUs")}</td>
						<td className={zebraDataColumnClass(0, "metric")}>{t("maxSizeMetric")}</td>
					</tr>
					<tr>
						<td className={zebraLabelClass(1)}>{t("volumeLabel")}</td>
						<td className={zebraDataColumnClass(1, "us")}>{t("volumeUs")}</td>
						<td className={zebraDataColumnClass(1, "metric")}>{t("volumeMetric")}</td>
					</tr>
					<tr>
						<td className={zebraLabelClass(2)}>{t("depthLabel")}</td>
						<td className={zebraDataColumnClass(2, "us")}>{t("depthRow1Us")}</td>
						<td className={zebraDataColumnClass(2, "metric")}>{t("depthRow1Metric")}</td>
					</tr>
					<tr>
						<td className={zebraLabelClass(3, "rounded-bl-[15px] border-b-0")}>
							{t("projectedLabel")}
						</td>
						<td className={zebraDataColumnClass(3, "us", "border-b-0")}>
							{t("projectedUsCombined")}
						</td>
						<td className={zebraDataColumnClass(3, "metric", "rounded-br-[15px] border-b-0")}>
							{t("projectedMetricCombined")}
						</td>
					</tr>
								</>
							) : (
								<>
					<tr>
						<td className={tdLabel}>{t("maxSizeLabel")}</td>
						<td className={tdDataUs}>{t("maxSizeUs")}</td>
						<td className={tdDataMetric}>{t("maxSizeMetric")}</td>
					</tr>
					<tr>
						<td className={tdLabel}>{t("volumeLabel")}</td>
						<td className={tdDataUs}>{t("volumeUs")}</td>
						<td className={tdDataMetric}>{t("volumeMetric")}</td>
					</tr>
					<tr>
						<td className={tdLabel} rowSpan={2}>
							{t("depthLabel")}
						</td>
						<td className={tdDataUs}>{t("depthRow1Us")}</td>
						<td className={tdDataMetric}>{t("depthRow1Metric")}</td>
					</tr>
					<tr>
						<td className={tdDataUs}>{t("depthRow2Us")}</td>
						<td className={tdDataMetric}>{t("depthRow2Metric")}</td>
					</tr>
					<tr>
						<td className={`${tdLabel} rounded-bl-[15px] border-b-0`} rowSpan={2}>
							{t("projectedLabel")}
						</td>
						<td className={tdDataUs}>{t("projectedRow1Us")}</td>
						<td className={tdDataMetric}>{t("projectedRow1Metric")}</td>
					</tr>
					<tr>
						<td className={`${tdDataUs} border-b-0`}>{t("projectedRow2Us")}</td>
						<td className={`${tdDataMetric} rounded-br-[15px] border-b-0`}>{t("projectedRow2Metric")}</td>
					</tr>
								</>
							)}
						</tbody>
					</table>
				</div>

				<div className="mx-12 mt-10 sm:mt-12">
					<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("footerHeading")}</h3>
					<ul className="list-disc space-y-4 pl-5 text-sm leading-relaxed text-black md:text-base">
						<li>{t("footerBullet1")}</li>
						<li>{t("footerBullet2")}</li>
						<li>{t("footerBullet3")}</li>
						<li>{t("footerBullet4")}</li>
						{insertMoldingCompactTable ? <li>{t("footerBullet5")}</li> : null}
					</ul>
				</div>
			</div>
		</section>
	);
}
